import { useState } from "react";

const topPicks = [
  { id: "oak-round", name: "Round Light Oak Coffee Table", description: "A clean round table in light oak — soft shape, warm tone, and endlessly versatile in any calm living room.", priceRange: "Mid-range", buttonLabel: "View Table Options", tag: "Editor's Pick", affiliateUrl: "https://amzn.to/4tANU1D" },
  { id: "ash-rect", name: "Ash Wood Rectangular Table", description: "A minimal rectangular table in pale ash — structured but warm, with a lower shelf for practical storage.", priceRange: "Mid-range", buttonLabel: "See Ash Tables", tag: "Most Practical", affiliateUrl: "https://amzn.to/4tANU1D" },
  { id: "oak-oval", name: "Oval Oak Coffee Table", description: "An oval silhouette that softens the room and works beautifully with a rounded sofa or curved seating.", priceRange: "Mid-range to Splurge", buttonLabel: "Explore Oval Tables", tag: "Most Elegant", affiliateUrl: "https://amzn.to/4tANU1D" },
];

const budgetTables = [
  { id: "b1", name: "Affordable Light Wood Table", description: "A budget-friendly option in a warm wood tone — simple, clean, and easy to style with a tray and ceramics.", priceRange: "Under $200", buttonLabel: "View Budget Tables", tag: "Best Value", affiliateUrl: "" },
  { id: "b2", name: "Simple Oak-Look Coffee Table", description: "A clean minimal table that keeps the palette warm without stretching the budget.", priceRange: "Under $250", buttonLabel: "See Affordable Options", tag: "Smart Pick", affiliateUrl: "" },
];

const midTables = [
  { id: "m1", name: "Solid Oak Coffee Table", description: "Solid oak construction with a matte oiled finish — built to last and beautiful with age.", priceRange: "$350–$700", buttonLabel: "View Oak Tables", tag: "Anchor Piece", affiliateUrl: "" },
  { id: "m2", name: "Scandinavian Wood Table", description: "A clean Scandinavian silhouette in pale ash — minimal, warm, and effortlessly calm.", priceRange: "$300–$600", buttonLabel: "Explore Mid-Range", tag: "Most Minimal", affiliateUrl: "" },
];

const splurgeTables = [
  { id: "s1", name: "Handcrafted Oak Coffee Table", description: "A handcrafted solid oak table with exceptional grain and a warm natural finish — the kind that becomes a centrepiece.", priceRange: "$800+", buttonLabel: "View Luxury Tables", tag: "Artisan Craft", affiliateUrl: "" },
  { id: "s2", name: "Designer Wood & Marble Table", description: "A light wood base with a subtle marble top — understated luxury for a calm, considered room.", priceRange: "$1,000+", buttonLabel: "Explore Premium Tables", tag: "Statement Piece", affiliateUrl: "" },
];

const pairWith = [
  { id: "sofa", name: "Beige Linen Sofa", description: "The natural companion — a warm beige sofa that the coffee table anchors beautifully.", priceRange: "Mid-range", buttonLabel: "View Sofa Options", tag: "Perfect Pair", affiliateUrl: "https://amzn.to/4sd5Qyh" },
  { id: "rug", name: "Neutral Area Rug", description: "A soft neutral rug underneath defines the seating area and grounds the table.", priceRange: "Budget to Mid-range", buttonLabel: "View Rug Options", tag: "Essential Layer", affiliateUrl: "https://amzn.to/4dx4Ykf" },
  { id: "curtains", name: "Linen Curtains", description: "Soft linen curtains that echo the warmth of the wood and complete the neutral palette.", priceRange: "Budget to Mid-range", buttonLabel: "See Curtain Options", tag: "Easy Upgrade", affiliateUrl: "https://amzn.to/4tz8yPK" },
  { id: "tray", name: "Wooden Tray & Ceramics", description: "A simple tray with a ceramic vase and a candle — the easiest way to style a coffee table.", priceRange: "Budget", buttonLabel: "View Styling Options", tag: "Finishing Touch", affiliateUrl: "" },
];

const faqs = [
  { q: "What wood tone works best with a beige sofa?", a: "Light oak, ash, and pale walnut are the most versatile choices. They add warmth and natural contrast without competing with the sofa. Avoid very dark woods like ebony or dark walnut — they can feel heavy in a calm neutral room. The goal is a gentle contrast that adds depth without disrupting the palette." },
  { q: "What size coffee table do I need?", a: "As a general rule, the table should be roughly two-thirds the length of the sofa. Leave 40–45cm between the table and the sofa edge — enough to reach comfortably without crowding the space. In terms of height, aim for roughly the same height as the sofa seat cushion, around 40–45cm." },
  { q: "Round or rectangular — which is better?", a: "It depends on the room. Round and oval tables soften a space and work beautifully with curved sofas or in smaller rooms where you need to move around the table easily. Rectangular tables suit longer sofas and more structured, symmetrical layouts. If in doubt, round is almost always the more forgiving choice." },
  { q: "How do I style a wood coffee table in a neutral room?", a: "Keep it simple. A wooden tray with a small ceramic vase, a candle, and one or two books is all you need. The tray corrals the objects and makes the styling look intentional. Avoid cluttering the table — in a calm room, negative space is part of the design." },
  { q: "Is solid wood worth the investment?", a: "Yes, if you can stretch to it. Solid wood improves with age — it develops a patina, small marks add character, and it can be sanded and refinished if needed. Veneer and engineered wood options can look great initially but rarely age as well. For a piece you will use every day, solid wood is worth the investment." },
];

function ProductCard({ name, description, priceRange, buttonLabel, tag, affiliateUrl }: {
  name: string; description: string; priceRange: string; buttonLabel: string; tag: string; affiliateUrl: string;
}) {
  const [hovered, setHovered] = useState(false);
  const hasLink = affiliateUrl.trim().length > 0;
  return (
    <div
      className="border border-[#EDE6DA] rounded-xl p-6 bg-[#FDFAF5] hover:border-[#C8BAA8] transition-all duration-300 flex flex-col gap-4"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F1E8]">
          <i className="ri-table-line text-[#8B8378] text-base" />
        </div>
        <span className="text-[#B5A898] text-[9px] tracking-[0.35em] uppercase border border-[#EDE6DA] px-2.5 py-1 rounded-full" style={{ fontFamily: "'Inter', sans-serif" }}>{tag}</span>
      </div>
      <h3 className="text-[#2C2825] text-lg leading-snug" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>{name}</h3>
      <p className="text-[#8B8378] text-sm leading-relaxed flex-1" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{description}</p>
      <div className="flex items-center gap-2">
        <span className="text-[#B5A898] text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>Price</span>
        <span className="w-4 h-px bg-[#EDE6DA]" />
        <span className="text-[#6B5E53] text-xs" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>{priceRange}</span>
      </div>
      {hasLink ? (
        <a
          href={affiliateUrl}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="mt-1 self-start inline-flex items-center gap-2 border border-[#C8BAA8] text-[#6B5E53] px-5 py-2.5 rounded-full text-xs tracking-[0.14em] hover:bg-[#2C2825] hover:text-white hover:border-[#2C2825] transition-all duration-300 cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {buttonLabel}
          <span className="w-3 h-3 flex items-center justify-center">
            <i className={`ri-arrow-right-line text-[10px] transition-transform duration-200 ${hovered ? "translate-x-0.5" : ""}`} />
          </span>
        </a>
      ) : (
        <div
          className="mt-1 self-start inline-flex items-center gap-2 border border-[#EDE6DA] text-[#C8BAA8] px-5 py-2.5 rounded-full text-xs tracking-[0.14em] whitespace-nowrap select-none"
          style={{ fontFamily: "'Inter', sans-serif" }}
          title="Link coming soon"
        >
          {buttonLabel}
          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line text-[10px]" /></span>
        </div>
      )}
    </div>
  );
}

function Grid2({ items }: { items: typeof budgetTables }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{items.map(p => <ProductCard key={p.id} {...p} />)}</div>;
}

function Grid3({ items }: { items: typeof topPicks }) {
  return <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">{items.map(p => <ProductCard key={p.id} {...p} />)}</div>;
}

function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-5 mb-10">
      <div className="flex-1 h-px bg-[#EDE6DA]" />
      <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase flex-shrink-0" style={{ fontFamily: "'Inter', sans-serif" }}>{label}</p>
      <div className="flex-1 h-px bg-[#EDE6DA]" />
    </div>
  );
}

export default function WCTShopSection() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section className="max-w-2xl mx-auto px-4 md:px-0 py-4 mb-16">

      <div id="top-picks" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Table Edit</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Our Top Light Wood Coffee Table Picks</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          Chosen for their warm tone, natural grain, and ability to anchor a calm living room without overpowering it.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <Grid3 items={topPicks} />
      </div>

      <Divider label="By Budget" />

      <div id="budget-tables" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Budget</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Best Light Wood Tables Under $250</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          A well-chosen coffee table does not have to be expensive. These picks keep the tone warm and the look considered.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <Grid2 items={budgetTables} />
      </div>

      <div id="mid-tables" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Mid-Range</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Elevated Tables $300–$700</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          In this range, material quality and construction improve noticeably. These tables are built to last and styled to feel genuinely refined.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <Grid2 items={midTables} />
      </div>

      <div id="splurge-tables" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Luxury</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Splurge Picks — Luxury Wood Tables</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          For a more elevated result, these tables offer exceptional craftsmanship, considered grain, and the kind of quiet presence that makes a room feel complete.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <Grid2 items={splurgeTables} />
      </div>

      <Divider label="Complete the Look" />

      <div id="pair-with" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Style It</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>What to Pair With a Light Wood Coffee Table</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          A light wood table works best when the rest of the room stays warm and layered. These are the pieces that complete the look.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <Grid2 items={pairWith} />
      </div>

      {/* FAQ */}
      <div id="faq" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>FAQ</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Frequently Asked Questions</h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-[#EDE6DA] rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#F5F1E8]/50 transition-colors duration-200"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span className="text-[#2C2825] text-sm leading-snug pr-4" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>{faq.q}</span>
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`ri-add-line text-[#8B8378] text-base transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </span>
              </button>
              {openFaq === i && (
                <div className="px-6 pb-5">
                  <p className="text-[#6B5E53] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Conclusion */}
      <div className="mb-4">
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <p className="text-[#4A3F38] text-base md:text-lg leading-relaxed mb-5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          A light wood coffee table is one of the most rewarding investments in a calm room.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          It adds warmth, natural texture, and quiet structure — and it only gets better with age. Choose solid wood if you can,
          keep the tone light and warm, and let the grain do the work. Style it simply, and let the rest of the room breathe around it.
        </p>
      </div>

    </section>
  );
}
